import { type PspMethodUnion, type MethodUnion, type TransactionStateUnion, type IssuerEnum, PspEnum, type UserAccount, type PspUnion, type PaymentMethod, TransactionStateEnum, PspMethodEnum, MethodEnum } from '@payments/types'
import type { UIAlertProps } from '@core/types'
import { ref, computed, useState, firstToUpper, useSeamlessWalletAccountDetails, useFormat, mediaUrl } from '#imports'

// Minimal shape `formattedPaymentMethod` needs, for callers holding only a withdrawal transaction's fields.
type FormattablePaymentMethod = {
    paymentMethod: MethodUnion
    publicIdentifier: UserAccount['publicIdentifier']
}

export const usePayments = () => {
    const accountDetailsQuery = useSeamlessWalletAccountDetails()
    const { asMoney } = useFormat()

    const amount = useState<string | undefined>('payments:amount')
    const amountValue = useState<number>('payments:amount-value', () => 0)
    const isManualCorrectionActive = useState<boolean>('payments:is-manual-correction-active', () => false)
    const message = useState<UIAlertProps>('payments:message', () => ({}))
    const processing = useState<boolean>('payments:processing', () => false)
    const pspMethod = useState<Exclude<PspMethodUnion, PspMethodEnum.WITHDRAWAL>>('payments:psp-method')
    const receiptData = useState<any>('payments:receipt-data')
    const selectedAccount = useState<string>('payments:selected-account')

    const isReturning = ref<boolean>(false)

    const balance = computed(() => Number(accountDetailsQuery.data.value?.balance) - Number(accountDetailsQuery.data.value?.hosted_wallets?.total_balance))
    const hasBalance = computed(() => balance.value > 0)
    const isLoadingBalance = computed(() => accountDetailsQuery.status.value === 'pending')

    const isDeposit = computed((): boolean => pspMethod.value === PspMethodEnum.DEPOSIT)
    const isWithdraw = computed((): boolean => pspMethod.value === PspMethodEnum.WITHDRAW)

    const isPspPaymentIQ = (psp?: PspUnion) => psp && psp?.toLowerCase() === PspEnum.PAYMENTIQ.toLowerCase()
    const isPspNemKonto = (psp?: PspUnion) => psp && psp?.toLowerCase() === PspEnum.NEMKONTO.toLowerCase()
    const isPspEpay = (psp?: PspUnion) => psp && psp?.toLowerCase() === PspEnum.EPAY.toLowerCase()
    const isPspDummy = (psp?: PspUnion) => psp && psp?.toLowerCase() === PspEnum.DUMMY.toLowerCase()
    const isPspTrueLayer = (psp?: PspUnion) => psp && psp?.toLowerCase() === PspEnum.TRUELAYER.toLowerCase()

    const isCreditCard = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.CREDITCARD.toLowerCase()
    const isApplePay = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.APPLEPAY.toLowerCase()
    const isGooglePay = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.GOOGLEPAY.toLowerCase()
    const isBank = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.BANK.toLowerCase()
    const isTrueLayer = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.TRUELAYER.toLowerCase()
    const isMobilepay = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.MOBILEPAY.toLowerCase()
    const isPayPal = (type?: MethodUnion) => type && type.toLowerCase() === MethodEnum.PAYPAL.toLowerCase()

    // Regenerated client returns states UPPER_CASE; local union is lower-case, so accept either (normalised below).
    type TransactionStateInput = TransactionStateUnion | Uppercase<TransactionStateUnion>

    const isTransactionSuccessful = (state?: TransactionStateInput) => state && state.toLowerCase() === TransactionStateEnum.SUCCESSFUL
    const isTransactionFailed = (state?: TransactionStateInput) => state && state.toLowerCase() === TransactionStateEnum.FAILED
    const isTransactionInconsistent = (state?: TransactionStateInput) => state && state.toLowerCase() === TransactionStateEnum.INCONSISTENT

    function resetMessage(): void {
        message.value = {}
    }

    function accountImage(account: UserAccount): string | undefined {
        const type = account?.issuer ?? account.type

        if (!type) return mediaUrl('/media/cards/unknown.svg')

        let fileName: string

        switch (type as IssuerEnum | MethodEnum) {
            case MethodEnum.NEMKONTO:
                return ''

            case MethodEnum.GOOGLEPAY:
                fileName = 'googlepay'
                break

            case MethodEnum.BANK:
            case MethodEnum.TRUELAYER:
                fileName = 'truelayer'
                break

            case MethodEnum.MOBILEPAY:
                fileName = 'mobilepay-plain'
                break

            case MethodEnum.PAYPAL:
                fileName = 'paypal-plain'
                break

            default:
                fileName = type.toLowerCase()
                break
        }

        return mediaUrl(`/media/cards/${fileName}.svg`)
    }

    function formatCardNumber(cardNumber: string): string {
        return cardNumber.replace(/(.{4})/g, '$1 ')
    }

    function formattedPaymentMethod(account: UserAccount | PaymentMethod | FormattablePaymentMethod): string {
        const type = (account as UserAccount)?.type || (account as PaymentMethod)?.paymentMethod

        if (!type) return ''

        if (isWithdraw.value && type !== MethodEnum.CREDITCARD && (account as UserAccount)?.publicIdentifier) {
            return (account as UserAccount).publicIdentifier as string
        }

        if (type === MethodEnum.CREDITCARD) {
            if ((account as UserAccount)?.publicIdentifier) return formatCardNumber((account as UserAccount).publicIdentifier as string)
        }

        if ([MethodEnum.BANK, MethodEnum.TRUELAYER].includes(type)) return 'Instant Bank Payment'
        if (type === MethodEnum.APPLEPAY) return 'Apple Pay'
        if (type === MethodEnum.MOBILEPAY) return 'MobilePay'
        if (type === MethodEnum.PAYPAL) return 'PayPal'
        if (type === MethodEnum.NEMKONTO) return 'NemKonto'
        if (type === MethodEnum.GOOGLEPAY) return 'Google Pay'

        return firstToUpper(type)
    }

    function setSelectedAmount(selectedAmount: string | number): void {
        if (processing.value) return

        amount.value = asMoney(Number(selectedAmount), { removeTrailingZeros: true }) as string
    }

    return {
        amount,
        amountValue,
        pspMethod,
        isManualCorrectionActive,
        message,
        processing,
        selectedAccount,
        balance,
        hasBalance,
        isLoadingBalance,
        isReturning,
        isDeposit,
        isWithdraw,
        isPspPaymentIQ,
        isPspNemKonto,
        isPspEpay,
        isPspDummy,
        isPspTrueLayer,
        isCreditCard,
        isApplePay,
        isGooglePay,
        isBank,
        isTrueLayer,
        isMobilepay,
        isPayPal,
        isTransactionSuccessful,
        isTransactionFailed,
        isTransactionInconsistent,
        receiptData,
        resetMessage,
        setSelectedAmount,
        accountDetailsQuery,
        accountImage,
        formatCardNumber,
        formattedPaymentMethod,
    }
}
